import { MapOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import isNil from "lodash/isNil";
import { useMemo, useState } from "react";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";

/**
 * Google Maps panel (Static API).
 *
 * Uses the Maps Static API:
 *   https://maps.googleapis.com/maps/api/staticmap
 *
 * Why not the Embed API?
 *   The Embed iframe is interactive but (a) every click navigates to Google
 *   Maps in a new tab, which is unwanted for card previews, and (b) iframes
 *   can't be captured as images, which blocks the "save image" flow. The
 *   Static API returns a plain JPEG — no click-through, fully savable.
 *
 * Supports:
 *   - mapType="roadmap" | "satellite" | "hybrid" | "terrain"
 *   - radiusMiles: when set with a "satellite"/"hybrid" mapType, draws a
 *       semi-transparent circle of that radius around the center. Used by
 *       the aerial mode's 1/5/10/20-mile radius picker. Zoom auto-adjusts
 *       so the whole circle fits in view unless `zoom` is passed explicitly.
 *
 * Requires REACT_APP_GOOGLE_MAPS_KEY with Maps Static API enabled.
 */
// The Static Maps API has a `size:tiny/small/mid` keyword for smaller
// markers, but it strips the classic teardrop shape and inner dark
// detail — the marker renders as a flat colored glyph.
//
// To get a smaller pin that keeps the classic appearance, we point at one
// of Google's hosted mapfiles PNG markers via `icon:`. Those preserve the
// inner shadow at their native sizes.
//
//   small / tiny → mapfiles/marker_red.png   (20x34, classic teardrop)
//   mid          → mapfiles/ms/icons/red-dot.png (32x32, dot variant)
//   normal       → default red pin (full size with inner detail)
const PIN_ICON_URLS = {
  tiny: "https://maps.gstatic.com/mapfiles/marker_red.png",
  small: "https://maps.gstatic.com/mapfiles/marker_red.png",
  mid: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
};

const buildMarkersParam = (latitude, longitude, pinSize) => {
  const segments = [];
  const iconUrl = PIN_ICON_URLS[pinSize];
  if (iconUrl) {
    // Anchor the bottom-center of the icon over the lat/lng so the tip of
    // the teardrop sits on the actual coordinate (default for icon: is
    // top-left, which would offset the pin north-west of the address).
    segments.push("anchor:bottomcenter");
    segments.push(`icon:${iconUrl}`);
  } else {
    segments.push("color:red");
  }
  segments.push(`${latitude},${longitude}`);
  return segments.join("|");
};

const GoogleMapPanel = ({
  address,
  height = "200px",
  zoom,
  mapType = "roadmap",
  radiusMiles = null,
  pinSize = "normal",
  sx,
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const latitude = address?.latitude;
  const longitude = address?.longitude;

  const [loadFailed, setLoadFailed] = useState(false);

  const src = useMemo(() => {
    if (!apiKey || isNil(latitude) || isNil(longitude)) return null;

    const effectiveZoom = !isNil(zoom)
      ? zoom
      : radiusMiles
        ? zoomForRadius(radiusMiles, latitude)
        : 15;

    const params = new URLSearchParams({
      key: apiKey,
      center: `${latitude},${longitude}`,
      zoom: String(effectiveZoom),
      size: "640x400",
      scale: "2",
      maptype: mapType,
      markers: buildMarkersParam(latitude, longitude, pinSize),
    });

    let url = `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;

    if (radiusMiles && radiusMiles > 0) {
      const ring = circlePath(latitude, longitude, radiusMiles);
      // color / fillcolor use 0xRRGGBBAA — final byte is alpha.
      const pathSpec =
        `color:0x2962ffcc|fillcolor:0x2962ff33|weight:2|` + ring.join("|");
      // URLSearchParams encodes `|` which breaks path specs; append manually.
      url += `&path=${encodeURIComponent(pathSpec)}`;
    }

    return url;
  }, [apiKey, latitude, longitude, zoom, mapType, radiusMiles, pinSize]);

  if (!src || loadFailed) {
    const reason = !apiKey
      ? "Map key not configured"
      : loadFailed
        ? "Map failed to load"
        : "No location available";
    return (
      <Flx
        center
        column
        sx={{
          height,
          background: "#f5f5f5",
          gap: 1,
          ...sx,
        }}
      >
        <MapOutlined sx={{ color: "#666", fontSize: "2rem" }} />
        <Txt sx={{ color: "#666" }}>{reason}</Txt>
      </Flx>
    );
  }

  return (
    <Box sx={{ width: "100%", height, overflow: "hidden", ...sx }}>
      <img
        src={src}
        alt={mapType === "satellite" ? "Aerial view" : "Map"}
        onError={() => setLoadFailed(true)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </Box>
  );
};

/**
 * Expose the in-use static map URL without rendering, so callers (the
 * PropertyMap overlay) can pipe it into save / download flows.
 */
export const buildStaticMapUrl = ({
  apiKey,
  latitude,
  longitude,
  mapType = "roadmap",
  zoom,
  radiusMiles = null,
  pinSize = "normal",
  size = "1280x800",
}) => {
  if (!apiKey || isNil(latitude) || isNil(longitude)) return null;
  const effectiveZoom = !isNil(zoom)
    ? zoom
    : radiusMiles
      ? zoomForRadius(radiusMiles, latitude)
      : 15;
  const params = new URLSearchParams({
    key: apiKey,
    center: `${latitude},${longitude}`,
    zoom: String(effectiveZoom),
    size,
    scale: "2",
    maptype: mapType,
    markers: buildMarkersParam(latitude, longitude, pinSize),
  });
  let url = `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
  if (radiusMiles && radiusMiles > 0) {
    const ring = circlePath(latitude, longitude, radiusMiles);
    const pathSpec =
      `color:0x2962ffcc|fillcolor:0x2962ff33|weight:2|` + ring.join("|");
    url += `&path=${encodeURIComponent(pathSpec)}`;
  }
  return url;
};

/**
 * Smallest integer zoom at which the requested radius-circle diameter
 * meets or exceeds the image width in pixels. At this zoom the visible
 * frame sits inside the radius — the map content the user sees is
 * strictly within the circle (a goal of the aerial mode's radius picker).
 *
 * Derivation: at zoom Z and latitude φ, meters-per-pixel =
 *   40075017 · cos(φ) / (256 · 2^Z)
 * so diameter_px = 2 · r_meters · 2^Z · 256 / (40075017 · cos(φ)).
 * Solving for the smallest Z where diameter_px ≥ imageWidthPx and
 * rounding up keeps the circle ≥ the viewport at every integer zoom.
 */
const zoomForRadius = (radiusMiles, latitude = 40, imageWidthPx = 640) => {
  if (!radiusMiles || radiusMiles <= 0) return 15;
  const radiusMeters = radiusMiles * 1609.34;
  const cosLat = Math.cos((Math.abs(latitude) * Math.PI) / 180) || 1;
  const twoToZ =
    (imageWidthPx * 40075017 * cosLat) / (2 * radiusMeters * 256);
  const z = Math.ceil(Math.log2(twoToZ));
  return Math.max(1, Math.min(21, z));
};

/**
 * Generate N points around a center at a given mile radius, using the
 * spherical-earth bearing formula. Accurate enough for circles up to
 * hundreds of miles.
 */
const circlePath = (centerLat, centerLng, radiusMiles, numPoints = 48) => {
  const R = 3958.8;
  const points = [];
  const lat1 = (centerLat * Math.PI) / 180;
  const lng1 = (centerLng * Math.PI) / 180;
  const d = radiusMiles / R;
  for (let i = 0; i <= numPoints; i++) {
    const bearing = (i / numPoints) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(d) +
        Math.cos(lat1) * Math.sin(d) * Math.cos(bearing),
    );
    const lng2 =
      lng1 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat1),
        Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
      );
    points.push(
      `${((lat2 * 180) / Math.PI).toFixed(5)},${((lng2 * 180) / Math.PI).toFixed(5)}`,
    );
  }
  return points;
};

export default GoogleMapPanel;
